import { useCallback, useMemo } from 'react';
import { FixedSizeListProps } from 'react-window';

import { useWindowDimensions } from '~/hooks/useWindowDimensions';

type UseSearchDimensionsOutput = {
  infoBox: {
    height: number;
  };
  list: Pick<FixedSizeListProps, 'height' | 'width' | 'itemSize'>;
};

export const useSearchDimensions = (): UseSearchDimensionsOutput => {
  const windowDimensions = useWindowDimensions({ debounceDuration: 500 });

  const dimensionSetup = useMemo(() => {
    return {
      viewportHeights: {
        topBar: 50,
        footer: 50,
        viewBuffer: 20,
      },
      searchHeights: {
        item: 53,
        listBuffer: 8,
        tabs: 33,
        header: 29,
      },
      itemCount: {
        min: 2,
        max: 9,
      },
      searchWidth: 300,
    };
  }, []);

  const getAvailableSpace = useCallback(
    (windowHeight: number) => {
      const { viewportHeights, searchHeights } = dimensionSetup;
      const buffersAndContent =
        viewportHeights.topBar +
        viewportHeights.footer +
        viewportHeights.viewBuffer +
        (searchHeights.header + searchHeights.tabs + searchHeights.listBuffer);

      return windowHeight - buffersAndContent;
    },
    [dimensionSetup],
  );

  const getItemCount = useCallback(
    (windowHeight: number) => {
      const { searchHeights, itemCount } = dimensionSetup;
      const itemCountFitInAvailableSpace = Math.floor(
        getAvailableSpace(windowHeight) / searchHeights.item,
      );

      if (itemCountFitInAvailableSpace > itemCount.max) {
        return itemCount.max;
      }
      if (itemCountFitInAvailableSpace < itemCount.min) {
        return itemCount.min;
      }
      return itemCountFitInAvailableSpace;
    },
    [getAvailableSpace, dimensionSetup],
  );

  return useMemo((): UseSearchDimensionsOutput => {
    const { searchHeights, searchWidth } = dimensionSetup;
    const listHeight =
      searchHeights.item * getItemCount(windowDimensions.height) +
      searchHeights.listBuffer;

    return {
      infoBox: {
        height: listHeight + searchHeights.header + searchHeights.tabs,
      },
      list: {
        height: listHeight,
        width: searchWidth,
        itemSize: searchHeights.item,
      },
    };
  }, [dimensionSetup, getItemCount, windowDimensions.height]);
};
